import { Session } from '@ais-ui/auth';

import { convertFieldsToDates, remapHeaders } from '@ais-ui/utils';
import { reorderShipDataColumns } from './orderShipDataByGridCFG';

export type LegendType = 'intervalWithValue' | 'interval' | 'text';

export type Metric = 'ships' | 'speed' | 'temperature';

export type Unit =
  | 'count'
  | 'km/h'
  | 'degreesCelsius'
  | 'percentage'
  | 'knots'
  | 'meters'
  | 'degrees'
  | 'm3';

export interface IntervalWithValueItem {
  color: string;
  end_interval: number;
  start_interval: number;
  text: string;
  value: string;
}

export interface IntervalItem {
  color: string;
  end_interval: number;
  start_interval: number;
  text: string;
}

export interface TextItem {
  color: string;
  text: string;
}

export type Item = IntervalWithValueItem;

export interface LegendConfig<T extends LegendType> {
  type: T;
  metric: Metric;
  unit: Unit;
  title: string;
  subtitle?: string;
  items: T extends 'intervalWithValue'
    ? IntervalWithValueItem[]
    : T extends 'interval'
    ? IntervalItem[]
    : never;
}

type CheckQueueStatusResult = {
  errorMessage: string;
  percentComplete: number;
  status: 'QueueRunning' | 'QueueComplete';
};

export type ReportShipData = {
  [key: string]: any;
};

type JsonRpcResponseWithShipData = {
  data: string; // JSON string that can be parsed into ReportShipData[]
  exportFormats: string[];
  maxLat: number;
  maxLon: number;
  minLat: number;
  minLon: number;
};

export type GetOverlayParams = {
  jobUuid: string;
  centerLat: number;
  centerLon: number;
  zoomLevel: number;
  width: number;
  height: number;
  legendX: number;
  legendY: number;
  leftLon: number;
  rightLon: number;
};

export const startQueryingReportResult = async (
  session: Session,
  jobId: string
): Promise<string> => {
  const response: string = await session.request('queueJobResult', {
    jobUuid: jobId,
  });

  return response;
};

export const getReportQueueStatus = async (
  session: Session,
  queueId: string
): Promise<CheckQueueStatusResult> => {
  const response: CheckQueueStatusResult = await session.request(
    'getQueueStatus',
    {
      queueUuid: queueId,
    }
  );

  return response;
};

export interface ReportResult {
  shipData: ReportShipData[];
  exportFormats: string[];
  imageExtent: number[];
}

function extractShipData(
  response: JsonRpcResponseWithShipData
): ReportShipData[] {
  // Try parsing response.data first
  let parsedData;

  if (response.data) {
    try {
      parsedData = JSON.parse(response.data);
    } catch (error) {
      console.warn('Failed to parse response data as JSON:', error);
      parsedData = response.data;
    }
  }

  let shipData: ReportShipData[];
  const gridCfg = parsedData.GRIDCFG || []; // Use GRIDCFG if available

  // Check if DATA exists in the parsed data (normal scenario for reports)
  if (parsedData && parsedData.DATA !== undefined) {
    shipData = parsedData.DATA;
  } else {
    // If DATA doesn't exist, check for data at root level (watchdog report)
    shipData = parsedData || response.data || [];
  }

  // Apply column reordering if GRIDCFG is provided
  if (gridCfg && Array.isArray(shipData)) {
    shipData = reorderShipDataColumns(shipData, gridCfg);
  }

  return shipData;
}

export const getReportResult = async (
  session: Session,
  queueId: string,
  timeZone: string
): Promise<ReportResult> => {
  const response: JsonRpcResponseWithShipData = await session.request(
    'getJobResult',
    {
      queueUuid: queueId,
    }
  );

  let shipData: ReportShipData[] = [];

  if (response.data && response.data !== 'null') {
    shipData = extractShipData(response);

    if (shipData) {
      convertFieldsToDates(shipData, [
        'Departure',
        'departure',
        'Arrival',
        'arrival',
        'Date',
        'date',
        'DATE',
        'ata',
        'ATA',
        'atd',
        'ATD',
        'Begin',
        'begin',
        'End',
        'end',
        'Exit',
        'exit',
        'Enter',
        'enter',
        'Timestamp',
        'timestamp',
        'Dats',
        'DATS',
        'dats',
        'SDATS',
        'sdats',
        'Sdats',
        'EDATS',
        'edats',
        'Edats',
        'CPAT',
        'Cpat',
        'cpat',
        'ACTTS',
        'Actts',
        'actts',
        'DEATS',
        'Deats',
        'deats',
        'ETA',
        'Eta',
        'eta',
        'ACT',
        'Act',
        'act',
        'DEA',
        'Dea',
        'dea',
      ]);
      shipData = remapHeaders(shipData);
    }
  }

  const exportFormats = response.exportFormats;
  const imageExtent = [
    response.minLon,
    response.minLat,
    response.maxLon,
    response.maxLat,
  ];

  return { shipData, exportFormats, imageExtent };
};

export const getReportOverlayAISWeb = async (
  session: Session,
  params: GetOverlayParams
): Promise<string> => {
  const response: string = await session.request('getOverlay', {
    interface: 'IReport',
    service_type: 'Report',
    ran: 3,
    serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    ...params,
  });
  return response;
};

export type ReportOverlayResponse = {
  overlay: string;
  legend: LegendConfig<LegendType>;
};

export const getReportOverlayMP = async (
  session: Session,
  params: GetOverlayParams,
  timeZone: string
): Promise<ReportOverlayResponse> => {
  const response = await session.request('getReportOverlayMP', {
    interface: 'IReport',
    service_type: 'Report',
    ran: 3,
    serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    ...params,
    timeZone: timeZone,
  });

  return response;
};
